import { defineRecipe, defineSlotRecipe } from "@pandacss/dev";

export const aromaDraggableContainer = defineRecipe({
  className: "aroma-draggable-container",
  jsx: ["DraggableContainer"],
  base: {
    display: "flex",
    flexDirection: "column",
    gap: "gap.2",
  },
});

export const aromaDraggableItem = defineSlotRecipe({
  className: "aroma-draggable-item",
  slots: ["wrapper", "knob", "content"],
  jsx: ["DraggableContainer"],
  base: {
    wrapper: {
      maxWidth: "100%",
      overflow: "hidden",
      opacity: "0.999", // to fix the issue with the drag preview's border-radius bg color https://bugs.webkit.org/show_bug.cgi?id=108079
      display: "flex",
      flexGrow: "1",
      borderColor: "border.neutral.bold",
      borderWidth: "m",
      borderRadius: "l",
      padding: "calc({spacing.padding.2} - {borderWidths.m})",
      gap: "gap.2",
      bgColor: "background.neutral.base.subtlest",
      _notDisabled: {
        _hover: {
          borderColor: "border.neutral.bold-hover",
          boxShadow: "effect.shadow.bold-bottom",
        },
      },
      _focusVisible: {
        outline: "none",
      },
    },
    knob: {
      cursor: "grab",
      height: "m",
      width: "m",
      borderRadius: "s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      _groupHover: {
        bgColor: "background.neutral.base.subtlest-hover",
      },
      _groupDisabled: {
        cursor: "default",
        color: "content.neutral.base.disable",
        bgColor: "background.neutral.disable.subtle",
      },
      _active: {
        cursor: "grabbing",
      },
    },
    content: {
      width: "100%",
      maxWidth: "calc(100% - {sizes.xl})",
      overflow: "hidden",
    },
  },
  variants: {
    status: {
      warning: {
        wrapper: {
          borderColor: "border.functional.warning",
          _focusVisible: {
            shadow: "effect.focus-ring.warning",
          },
        },
        knob: {
          _groupFocusVisible: {
            color: "content.functional.warning",
          },
        },
      },
      error: {
        wrapper: {
          borderColor: "border.functional.error",
          _focusVisible: {
            shadow: "effect.focus-ring.error",
          },
        },
        knob: {
          _groupFocusVisible: {
            color: "content.functional.error",
          },
        },
      },
      default: {
        wrapper: {
          _focusVisible: {
            borderColor: "border.brand-bold",
            shadow: "effect.focus-ring.brand",
          },
        },
        knob: {
          _groupFocusVisible: {
            color: "content.brand.bold",
          },
        },
      },
    },
    ghost: {
      true: {
        wrapper: {
          bgColor: "background.neutral.base.subtler!",
          borderColor: "transparent!",
        },
        knob: {
          opacity: "0",
        },
        content: {
          opacity: "0",
        },
      },
    },
    active: {
      true: {
        wrapper: {
          borderColor: "border.brand-bold",
          boxShadow: "effect.shadow.bolder-bottom",
        },
        knob: {
          bgColor: "background.brand.subtle-active",
          color: "content.brand.bold",
        },
      },
    },
    withoutAnimation: {
      true: {
        wrapper: {
          transition: "none !important",
          transform: "none !important",
        },
      },
    },
  },
  defaultVariants: {
    status: "default",
  },
  compoundVariants: [
    {
      active: true,
      status: "warning",
      css: {
        wrapper: {
          borderColor: "border.functional.warning",
        },
        knob: {
          bgColor: "background.functional.warning.subtle-active",
          color: "content.functional.warning",
        },
      },
    },
    {
      active: true,
      status: "error",
      css: {
        wrapper: {
          borderColor: "border.functional.error",
        },
        knob: {
          bgColor: "background.functional.error.subtle-active",
          color: "content.functional.error",
        },
      },
    },
  ],
});

export const aromaDraggableSignifier = defineSlotRecipe({
  className: "aroma-draggable-container-signifier",
  jsx: ["Signifier"],
  slots: ["wrapper", "line", "ring"],
  base: {
    wrapper: {
      position: "absolute",
      display: "flex",
      alignItems: "center",
      flexDirection: "row",
    },
    line: {
      borderColor: "border.brand-bold",
      borderWidth: "m",
      width: "100%",
      height: "0",
    },
    ring: {
      width: "4xs",
      height: "4xs",
      borderColor: "border.brand-bold",
      borderRadius: "pill",
      borderWidth: "l",
      flexShrink: 0,
    },
  },
  variants: {
    edge: {
      top: {
        wrapper: {
          top: "-padding.2",
          width: "calc(100% + {sizes.2xs})",
          left: "-padding.2",
        },
      },
      bottom: {
        wrapper: {
          bottom: "-padding.2",
          width: "calc(100% + {sizes.2xs})",
          left: "-padding.2",
        },
      },
      left: {
        wrapper: {
          flexDirection: "column",
          height: "calc(100% + {sizes.2xs})",
          top: "-padding.2",
          left: "-padding.2",
        },
        line: {
          width: "0",
          height: "100%",
        },
      },
      right: {
        wrapper: {
          flexDirection: "column",
          height: "calc(100% + {sizes.2xs})",
          top: "-padding.2",
          right: "-padding.2",
        },
        line: {
          width: "0",
          height: "100%",
        },
      },
    },
  },
});
